import {
    createContext,
    ReactNode,
    useContext,
    useEffect,
    useState,
} from "react"
import api, { setupAxiosInterceptors } from "../utils/axios"

interface AuthContextType {
    isAuthenticated: boolean
    user: { id: string; username: string } | null
    login: (username: string, password: string) => Promise<boolean>
    logout: () => void
    loading: boolean
    register: (
        username: string,
        password: string,
        onError: (error: string) => void,
        callback: () => void
    ) => Promise<boolean>
}

function handlerError(error: any, fb?: string): string {
    if (error.response) {
        return error.response.data.error || fb || "Erro desconhecido"
    }
    return fb || "Erro desconhecido"
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const AuthProvider = ({ children }: { children: ReactNode }) => {
    // O token vive em cookie httpOnly (ilegível ao JS). O sinal de sessão no
    // client é a presença do `user` no localStorage; se o cookie tiver expirado,
    // a 1ª request 401 redireciona ao login (interceptor do axios).
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(
        localStorage.getItem("user") !== null
    )
    const [user, setUser] = useState<{ id: string; username: string } | null>(
        localStorage.getItem("user")
            ? JSON.parse(localStorage.getItem("user")!)
            : null
    )
    const [loading, setLoading] = useState<boolean>(false)

    useEffect(() => {
        setupAxiosInterceptors()
    }, [])

    const login = async (
        username: string,
        password: string
    ): Promise<boolean> => {
        try {
            setLoading(true)
            const response = await api.post(`/login`, {
                username,
                password,
            })
            const { success, data, error } = response.data

            if (!success) {
                return false
            }
            const { user: loggedUser } = data
            if (loggedUser) {
                setUser({
                    id: loggedUser.id,
                    username: loggedUser.username,
                })
                localStorage.setItem("user", JSON.stringify(loggedUser))
                setIsAuthenticated(true)
                return true
            }
            return false
        } catch {
            return false
        } finally {
            setLoading(false)
        }
    }

    const register = async (
        username: string,
        password: string,
        onError: (error: string) => void,
        onSuccess: () => void
    ): Promise<boolean> => {
        try {
            setLoading(true)
            const response = await api.post(`/register`, {
                username,
                password,
            })
            const { success, data, error } = response.data
            if (!success) {
                onError(handlerError(error) || "Erro ao registrar")
                return false
            }
            const { user: registeredUser } = data
            if (registeredUser) {
                setUser({
                    id: registeredUser.id,
                    username: registeredUser.username,
                })
                // BUG FIX: o register não persistia o user → após registrar,
                // isAuthenticated era falso no F5 e as requests iam sem sessão.
                localStorage.setItem("user", JSON.stringify(registeredUser))
                setIsAuthenticated(true)
                onSuccess()
                return true
            } else {
                onError("Dados de registro inválidos")
                return false
            }
        } catch (error: any) {
            onError(handlerError(error, "Erro ao registrar"))
            return false
        } finally {
            setLoading(false)
        }
    }

    const logout = () => {
        // /logout limpa o cookie httpOnly no backend (o JS não consegue).
        api.post("/logout").catch(() => {})
        localStorage.removeItem("user")
        setUser(null)
        setIsAuthenticated(false)
        window.location.href = "/login"
    }

    return (
        <AuthContext.Provider
            value={{ isAuthenticated, user, login, logout, loading, register }}
        >
            {children}
        </AuthContext.Provider>
    )
}

export const useAuth = () => {
    const context = useContext(AuthContext)
    if (context === undefined) {
        throw new Error("useAuth deve ser usado dentro de um AuthProvider")
    }
    return context
}
